import {useState, type MutableRefObject} from 'react';

import {
  newVehicleApi,
  type TrimFeatureAssignment,
  type TrimFeatureAssignmentInput,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';

import {handleCatalogueError} from '../catalogueUi';
import {
  ITEM_CHANGE_CANCELLED,
  type PricedItemChangeOutcome,
} from '../types/PricedItemChangeOutcome';

/** A trim's write of a batch, prepared on a fresh read; the batch sends it. */
export type TrimBatchWrite = {
  trim: TrimResponseBody;
  stored: TrimFeatureAssignment[];
  next: TrimFeatureAssignmentInput[];
};
/**
 * Sends the writes of several trims as one batch with one new pricelist version dialog (D107).
 * Rejects with the batch's error after `onTrimFailed` named the trim whose operation it refused.
 */
export type WriteTrims = (
  writes: TrimBatchWrite[],
  onTrimFailed: (trimId: string) => void
) => Promise<PricedItemChangeOutcome>;

/**
 * The equipment matrix save of several trims as one batch: each trim is read fresh, one dialog lists
 * the rows of all of them, and one request writes all or nothing. A cancel or a failure keeps every
 * draft; a refusal that names one operation names its trim (`failedTrimId`).
 */
export function useTrimBatchSave<D>(props: {
  /** The matrix's queue of writes: the batch waits for the writes before it. */
  writes: MutableRefObject<Promise<unknown>>;
  writeTrims: WriteTrims;
  applyDrafts: (
    trimDrafts: D,
    assignments: TrimFeatureAssignment[]
  ) => TrimFeatureAssignmentInput[];
  /** The trim's drafts were written and its saved assignments read again. */
  onTrimSaved: (trimId: string, trimDrafts: D, saved: TrimFeatureAssignment[]) => void;
  /** The batch joined the open price list change group: nothing was written. */
  onTrimGrouped: (trimId: string, trimDrafts: D) => void;
  /** The batch failed: nothing was written. */
  onTrimFailed: (trimId: string) => void;
}) {
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const [failedTrimId, setFailedTrimId] = useState<string>();

  const saveTrims = (changedTrims: ReadonlyArray<readonly [string, D]>) => {
    const operation = props.writes.current
      .catch(() => undefined)
      .then(async () => {
        const prepared: TrimBatchWrite[] = [];
        for (const [trimId, trimDrafts] of changedTrims) {
          const trim = await getTrim({trimId}, false).unwrap();
          const assignments = await getAssignments({trimId}, false).unwrap();
          prepared.push({
            trim,
            stored: assignments,
            next: props.applyDrafts(trimDrafts, assignments),
          });
        }
        return props.writeTrims(prepared, setFailedTrimId);
      });
    props.writes.current = operation;
    return operation
      .then(async (outcome) => {
        if (outcome === ITEM_CHANGE_CANCELLED) {
          return outcome;
        }
        if (outcome === 'grouped') {
          changedTrims.forEach(([trimId, trimDrafts]) => props.onTrimGrouped(trimId, trimDrafts));
          return outcome;
        }
        // The batch returns no assignments: each saved set is read before its drafts retire, so no
        // cell flickers back. A trim whose read failed keeps its drafts: the cache still holds the
        // values from before the save, and a new save writes the same values again.
        await Promise.all(
          changedTrims.map(([trimId, trimDrafts]) =>
            getAssignments({trimId}, false)
              .unwrap()
              .then(
                (saved) => props.onTrimSaved(trimId, trimDrafts, saved),
                () => undefined
              )
          )
        );
        return outcome;
      })
      .catch((error: unknown) => {
        handleCatalogueError(error);
        changedTrims.forEach(([trimId]) => props.onTrimFailed(trimId));
        return 'failed' as const;
      });
  };

  return {saveTrims, failedTrimId, clearFailedTrim: () => setFailedTrimId(undefined)};
}
